import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AiOutlineCopy, AiOutlineFilePdf, AiOutlinePrinter } from "react-icons/ai";
import { BsFiletypeXls } from "react-icons/bs";
import { FaShoppingBasket } from "react-icons/fa";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { errorAlert, infoAlert, noticeAlert } from "../../Components/Alerts";
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import { AddActionsButton } from "../../Components/AddElementsButton";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { purchaseColumns, purchaseMappings } from "../../Components/ExcelConverter/FinancialStatement";
import "../AccountingReportsCRUD/AccountsReportPage.scss";
import "../../Style/__button.scss";

const currencyFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

function formatDateForDisplay(date) {
    if (!date) {
        return "";
    }

    if (date instanceof Date) {
        if (Number.isNaN(date.getTime())) {
            return "";
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }

    const raw = String(date).trim();
    if (!raw) {
        return "";
    }

    const parsedDate = new Date(raw);
    if (Number.isNaN(parsedDate.getTime())) {
        return raw;
    }

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();
    return `${day}.${month}.${year}`;
}

export default function MonthlyPurchaseReportPage() {
    const headers = authHeader();
    const { fiscalYear } = useContext(FiscalYearContext);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [quickFilter, setQuickFilter] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [totals, setTotals] = useState({
        totalTransactions: 0,
        totalAmount0: 0,
        totalAmount8: 0,
        totalAmount18: 0,
        totalWithoutVat: 0,
        totalReassessment: 0,
        totalWithoutReassessment: 0,
        totalWithVat: 0,
    });
    const [allRowsForCopy, setAllRowsForCopy] = useState([]);
    const [companiesData, setCompaniesData] = useState([]);
    const [logoPath, setLogoPath] = useState("");

    const buildParams = (page, pageSize, sort, filter, quick) => ({
        fiscalYear,
        page,
        pageSize,
        sort: JSON.stringify(sort || []),
        filter: JSON.stringify(filter || []),
        quickFilter: (quick || []).join(" "),
    });

    const fetchCompaniesData = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
            const companyItems = Array.isArray(response?.data) ? response.data : [];
            setCompaniesData(companyItems);
            setLogoPath(companyItems[0]?.CompanyLogo ? ApiLink.PHOTO_URL + companyItems[0].CompanyLogo : "");
        } catch (error) {
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave të kompanisë: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(ApiLink.API_URL + "FinanceStatement/MonthlyPurchaseReportPaged", {
                headers,
                params: buildParams(
                    paginationModel.page,
                    paginationModel.pageSize,
                    sortModel,
                    filterModel.items || [],
                    quickFilter,
                ),
            });

            const payload = response?.data || {};
            const items = payload?.items || payload?.Items || [];
            const payloadTotals = payload?.totals || payload?.Totals || {};

            setData(Array.isArray(items) ? items : []);
            setRowCount(Number(payload?.total ?? payload?.Total ?? items.length ?? 0));
            setTotals({
                totalTransactions: Number(payloadTotals.totalTransactions ?? payloadTotals.TotalTransactions ?? 0),
                totalAmount0: Number(payloadTotals.totalAmount0 ?? payloadTotals.TotalAmount0 ?? 0),
                totalAmount8: Number(payloadTotals.totalAmount8 ?? payloadTotals.TotalAmount8 ?? 0),
                totalAmount18: Number(payloadTotals.totalAmount18 ?? payloadTotals.TotalAmount18 ?? 0),
                totalWithoutVat: Number(payloadTotals.totalWithoutVat ?? payloadTotals.TotalWithoutVat ?? 0),
                totalReassessment: Number(payloadTotals.totalReassessment ?? payloadTotals.TotalReassessment ?? 0),
                totalWithoutReassessment: Number(payloadTotals.totalWithoutReassessment ?? payloadTotals.TotalWithoutReassessment ?? 0),
                totalWithVat: Number(payloadTotals.totalWithVat ?? payloadTotals.TotalWithVat ?? 0),
            });
        } catch (error) {
            if (error.response?.data?.message) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.message}`);
            } else if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fiscalYear, paginationModel, sortModel, filterModel, quickFilter]);

    useEffect(() => {
        fetchCompaniesData();
    }, [fiscalYear]);

    useEffect(() => {
        let isCancelled = false;

        const preloadAllRowsForCopy = async () => {
            try {
                const totalRows = Number(rowCount) || 0;
                if (totalRows <= 0) {
                    if (!isCancelled) {
                        setAllRowsForCopy([]);
                    }
                    return;
                }

                const items = await fetchAllRows();
                if (!isCancelled) {
                    setAllRowsForCopy(Array.isArray(items) ? items : []);
                }
            } catch (error) {
                if (!isCancelled) {
                    setAllRowsForCopy([]);
                }
            }
        };

        preloadAllRowsForCopy();

        return () => {
            isCancelled = true;
        };
    }, [fiscalYear, rowCount, sortModel, filterModel, quickFilter]);

    const fetchAllRows = async () => {
        const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);
        const response = await axios.get(ApiLink.API_URL + "FinanceStatement/MonthlyPurchaseReportPaged", {
            headers,
            params: buildParams(0, totalRowsToExport, sortModel, filterModel.items || [], quickFilter),
        });

        const payload = response?.data || {};
        const items = payload?.items || payload?.Items || [];
        return Array.isArray(items) ? items : [];
    };

    const mapExportData = (items) => (
        items.map((item) => ({
            Muaji: item.Muaji || "",
            Transaksione: Number(item.Transaksione || 0),
            Vlera0: Number(item.Vlera0 || 0),
            Vlera8: Number(item.Vlera8 || 0),
            Vlera18: Number(item.Vlera18 || 0),
            TotalPaTVSH: Number(item.TotalPaTVSH || 0),
            Rivleresimi: Number(item.Rivleresimi || 0),
            VleraPaRivleresim: Number(item.VleraPaRivleresim || 0),
            VleraMeTVSH: Number(item.VleraMeTVSH || 0),
        }))
    );

    const exportDatasetToExcel = (items, columns, mappings, fileName) => {
        if (!items || items.length === 0) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return;
        }

        const filteredData = items.map((item) => (
            columns.reduce((accumulator, column) => {
                accumulator[mappings[column] || column] = item[column];
                return accumulator;
            }, {})
        ));

        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(excelFile, `${fileName}.xlsx`);
    };

    const reportPeriodLabel = useMemo(() => `${fiscalYear}`, [fiscalYear]);

    const sanitizeFileName = (value, fallback) => {
        const cleaned = String(value || "")
            .trim()
            .replace(/[\\/:*?"<>|]/g, "")
            .replace(/\s+/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "");

        return cleaned || fallback;
    };

    const addLogo = (doc) => {
        if (!logoPath) {
            return;
        }

        doc.addImage(logoPath, "JPEG", 12, 10, 24, 24);
    };

    const addReportHeader = (doc, title, subtitle, periodLabel) => {
        const company = companiesData[0];
        const pageWidth = doc.internal.pageSize.getWidth();

        addLogo(doc);

        doc.setDrawColor(40, 73, 69);
        doc.setLineWidth(0.5);
        doc.line(12, 38, pageWidth - 12, 38);

        doc.setTextColor(31, 58, 54);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text(title, 40, 18);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(95, 127, 115);
        doc.text(subtitle, 40, 24);

        doc.setFontSize(9);
        doc.setTextColor(31, 58, 54);
        doc.text(`Periudha: ${periodLabel}`, 12, 46);

        if (!company) {
            return 52;
        }

        const companyDetails = [
            company.CompanyName,
            `Numri Identifikues: ${company.IdentificationNumber || "-"}`,
            `Numri i TVSH-së: ${company.VATNumber || "-"}`,
            `Adresa: ${company.Address || "-"}`,
            `Tel: ${company.PhoneNumbers || "-"}`,
            `Email: ${company.Email || "-"}`,
        ];

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(31, 58, 54);
        doc.text(companyDetails[0], pageWidth - 12, 16, { align: "right" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(95, 127, 115);
        doc.text(companyDetails.slice(1), pageWidth - 12, 22, { align: "right" });

        return 52;
    };

    const addReportFooter = (doc, periodLabel, generatedOnLabel) => {
        const pageCount = doc.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const brandingLabel = "© www.accountree.net";
        const footerCenterLabel = `Gjeneruar më: ${generatedOnLabel} | ${brandingLabel}`;

        for (let page = 1; page <= pageCount; page += 1) {
            doc.setPage(page);
            doc.setDrawColor(212, 226, 219);
            doc.setLineWidth(0.3);
            doc.line(12, pageHeight - 10, pageWidth - 12, pageHeight - 10);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(95, 127, 115);
            doc.text(`Periudha: ${periodLabel}`, 12, pageHeight - 5.5);
            doc.text(footerCenterLabel, pageWidth / 2, pageHeight - 5.5, { align: "center" });
            doc.text(`Faqe ${page} / ${pageCount}`, pageWidth - 12, pageHeight - 5.5, { align: "right" });
        }
    };

    const addPdfTable = (doc, headersRow, bodyRows, footRow, startY, baseColumnStyles) => {
        const horizontalMargin = 12;
        const availableTableWidth = doc.internal.pageSize.getWidth() - (horizontalMargin * 2);
        const baseTableWidth = Object.values(baseColumnStyles).reduce(
            (sum, style) => sum + Number(style.cellWidth || 0),
            0,
        );
        const scaleRatio = baseTableWidth > 0 ? availableTableWidth / baseTableWidth : 1;
        const columnStyles = Object.entries(baseColumnStyles).reduce((accumulator, [key, style]) => {
            accumulator[key] = {
                ...style,
                cellWidth: Number((Number(style.cellWidth || 0) * scaleRatio).toFixed(3)),
            };
            return accumulator;
        }, {});

        doc.autoTable({
            head: [headersRow],
            body: bodyRows,
            foot: [footRow],
            startY,
            showHead: "firstPage",
            showFoot: "lastPage",
            margin: {
                top: 16,
                left: horizontalMargin,
                right: horizontalMargin,
                bottom: 16,
            },
            theme: "grid",
            styles: {
                fontSize: 7.5,
                cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
                lineColor: [212, 226, 219],
                lineWidth: 0.1,
                textColor: [31, 58, 54],
                overflow: "linebreak",
            },
            headStyles: {
                fillColor: [40, 73, 69],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                halign: "center",
                valign: "middle",
            },
            bodyStyles: {
                valign: "middle",
            },
            footStyles: {
                fillColor: [231, 240, 235],
                textColor: [31, 58, 54],
                fontStyle: "bold",
            },
            alternateRowStyles: {
                fillColor: [247, 250, 248],
            },
            didParseCell: (hookData) => {
                if (hookData.section !== "foot") {
                    return;
                }

                if (hookData.column.index >= 2) {
                    hookData.cell.styles.halign = "right";
                }
            },
            columnStyles,
        });
    };

    const buildPdfTableData = (items) => {
        let totalTransactions = 0;
        let totalAmount0 = 0;
        let totalAmount8 = 0;
        let totalAmount18 = 0;
        let totalWithoutVat = 0;
        let totalReassessment = 0;
        let totalWithoutReassessment = 0;
        let totalWithVat = 0;

        const bodyRows = items.map((item, index) => {
            totalTransactions += Number(item.Transaksione || 0);
            totalAmount0 += Number(item.Vlera0 || 0);
            totalAmount8 += Number(item.Vlera8 || 0);
            totalAmount18 += Number(item.Vlera18 || 0);
            totalWithoutVat += Number(item.TotalPaTVSH || 0);
            totalReassessment += Number(item.Rivleresimi || 0);
            totalWithoutReassessment += Number(item.VleraPaRivleresim || 0);
            totalWithVat += Number(item.VleraMeTVSH || 0);

            return [
                String(index + 1),
                item.Muaji || "",
                String(item.Transaksione || 0),
                formatCurrency(item.Vlera0),
                formatCurrency(item.Vlera8),
                formatCurrency(item.Vlera18),
                formatCurrency(item.TotalPaTVSH),
                formatCurrency(item.Rivleresimi),
                formatCurrency(item.VleraPaRivleresim),
                formatCurrency(item.VleraMeTVSH),
            ];
        });

        const footRow = [
            "",
            "Totali",
            String(totalTransactions),
            formatCurrency(totalAmount0),
            formatCurrency(totalAmount8),
            formatCurrency(totalAmount18),
            formatCurrency(totalWithoutVat),
            formatCurrency(totalReassessment),
            formatCurrency(totalWithoutReassessment),
            formatCurrency(totalWithVat),
        ];

        return { bodyRows, footRow };
    };

    const buildPdfDocument = async () => {
        const items = await fetchAllRows();
        if (!items.length) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return null;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const generatedOnLabel = formatDateForDisplay(new Date());
        const startY = addReportHeader(
            doc,
            "Raporti Mujor i Blerjeve",
            "Raport sintetik i grumbullimit të blerjeve sipas muajve të vitit fiskal",
            reportPeriodLabel,
        );
        const { bodyRows, footRow } = buildPdfTableData(items);

        addPdfTable(
            doc,
            ["#", "Muaji", "Transaksione", "Vlera 0%", "Vlera 8%", "Vlera 18%", "Totali pa TVSH", "Rivlerësimi", "Vlera pa rivlerësim", "Vlera me TVSH"],
            bodyRows,
            footRow,
            startY,
            {
                0: { cellWidth: 8, halign: "center" },
                1: { cellWidth: 24 },
                2: { cellWidth: 16, halign: "right" },
                3: { cellWidth: 16, halign: "right" },
                4: { cellWidth: 16, halign: "right" },
                5: { cellWidth: 16, halign: "right" },
                6: { cellWidth: 20, halign: "right" },
                7: { cellWidth: 18, halign: "right" },
                8: { cellWidth: 24, halign: "right" },
                9: { cellWidth: 18, halign: "right" },
            },
        );

        addReportFooter(doc, reportPeriodLabel, generatedOnLabel);

        return {
            doc,
            companyNameForFile: sanitizeFileName(companiesData?.[0]?.CompanyName, "Kompania"),
        };
    };

    const exportPdf = async () => {
        const generatedDocument = await buildPdfDocument();
        if (!generatedDocument) {
            return;
        }

        const { doc, companyNameForFile } = generatedDocument;
        doc.save(`Raporti_Mujor_i_Blerjeve_${fiscalYear}_${companyNameForFile}.pdf`);
    };

    const printPdfDocument = (doc) => {
        const pdfBlob = doc.output("blob");
        const printUrl = URL.createObjectURL(pdfBlob);
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "fixed";
        printFrame.style.left = "-10000px";
        printFrame.style.top = "0";
        printFrame.style.width = "1024px";
        printFrame.style.height = "768px";
        printFrame.style.border = "0";
        printFrame.style.opacity = "0";
        printFrame.style.pointerEvents = "none";
        let didPrint = false;
        let cleanupDone = false;

        const cleanup = () => {
            if (cleanupDone) {
                return;
            }

            cleanupDone = true;
            URL.revokeObjectURL(printUrl);
            if (printFrame.parentNode) {
                printFrame.parentNode.removeChild(printFrame);
            }
        };

        printFrame.onload = () => {
            if (didPrint) {
                return;
            }

            didPrint = true;

            try {
                const frameWindow = printFrame.contentWindow;
                if (!frameWindow) {
                    cleanup();
                    errorAlert("Printimi nuk u inicializua. Ju lutem provoni përsëri.");
                    return;
                }

                window.setTimeout(() => {
                    frameWindow.focus();
                    frameWindow.onafterprint = cleanup;
                    frameWindow.print();
                    window.setTimeout(cleanup, 5000);
                }, 450);
            } catch (error) {
                cleanup();
                errorAlert("Nuk u hap dialogu i printimit. Ju lutem provoni përsëri.");
            }
        };

        document.body.appendChild(printFrame);
        printFrame.src = printUrl;

        window.setTimeout(() => {
            cleanup();
        }, 60000);
    };

    const handlePrintPdf = async () => {
        const generatedDocument = await buildPdfDocument();
        if (!generatedDocument) {
            return;
        }

        printPdfDocument(generatedDocument.doc);
    };

    const handleExportXlsx = async () => {
        const items = await fetchAllRows();
        exportDatasetToExcel(
            mapExportData(items),
            purchaseColumns,
            purchaseMappings,
            `Raporti_Mujor_i_Blerjeve_${fiscalYear}`,
        );
    };

    const handleCopyAllRows = () => {
        try {
            const items = allRowsForCopy.length ? allRowsForCopy : data;
            if (!items.length) {
                infoAlert("Nuk ka të dhëna për kopjim.");
                return;
            }

            const headersRow = [
                "Muaji",
                "Transaksione",
                "Vlera 0%",
                "Vlera 8%",
                "Vlera 18%",
                "Totali pa TVSH",
                "Rivlerësimi",
                "Vlera pa rivlerësim",
                "Vlera me TVSH",
            ];
            const lines = items.map((item) => ([
                item.Muaji || "",
                String(Number(item.Transaksione || 0)),
                formatCurrency(item.Vlera0),
                formatCurrency(item.Vlera8),
                formatCurrency(item.Vlera18),
                formatCurrency(item.TotalPaTVSH),
                formatCurrency(item.Rivleresimi),
                formatCurrency(item.VleraPaRivleresim),
                formatCurrency(item.VleraMeTVSH),
            ].join("\t")));
            const textToCopy = [headersRow.join("\t"), ...lines].join("\n");

            const copyUsingExecCommand = () => {
                const textArea = document.createElement("textarea");
                textArea.value = textToCopy;
                textArea.setAttribute("readonly", "");
                textArea.style.position = "fixed";
                textArea.style.left = "-9999px";
                document.body.appendChild(textArea);
                textArea.select();
                textArea.setSelectionRange(0, textArea.value.length);
                const isCopied = document.execCommand("copy");
                document.body.removeChild(textArea);
                return isCopied;
            };

            const isCopied = copyUsingExecCommand();
            if (isCopied) {
                noticeAlert("Linjat u kopjuan me sukses në clipboard.");
                return;
            }

            if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(textToCopy)
                    .then(() => {
                        noticeAlert("Linjat u kopjuan me sukses në clipboard.");
                    })
                    .catch(() => {
                        noticeAlert("Kopjimi automatik u kufizua nga Safari. Kliko përsëri butonin Kopjo.");
                    });
                return;
            }

            noticeAlert("Kopjimi automatik u kufizua nga Safari. Kliko përsëri butonin Kopjo.");
        } catch (error) {
            errorAlert("Kopjimi dështoi. Ju lutem provoni përsëri.");
        }
    };

    const columns = useMemo(() => ([
        { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
        { field: "monthLabel", headerName: "Muaji", minWidth: 170, width: 210 },
        { field: "transactions", headerName: "Transaksione", minWidth: 140, width: 160, valueFormatter: (value) => Number(value || 0) },
        { field: "amount0", headerName: "Vlera 0%", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "amount8", headerName: "Vlera 8%", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "amount18", headerName: "Vlera 18%", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "totalWithoutVat", headerName: "Totali pa TVSH", minWidth: 150, width: 170, valueFormatter: (value) => formatCurrency(value) },
        { field: "reassessment", headerName: "Rivlerësimi", minWidth: 140, width: 160, valueFormatter: (value) => formatCurrency(value) },
        { field: "amountWithoutReassessment", headerName: "Vlera pa rivlerësim", minWidth: 170, width: 190, valueFormatter: (value) => formatCurrency(value) },
        { field: "totalWithVat", headerName: "Vlera me TVSH", minWidth: 150, width: 170, valueFormatter: (value) => formatCurrency(value) },
    ]), []);

    const rows = useMemo(() => (
        data.map((item, index) => ({
            id: item.MonthNumber || `monthly-purchase-${index}`,
            rowNumber: paginationModel.page * paginationModel.pageSize + index + 1,
            monthLabel: item.Muaji || "",
            transactions: Number(item.Transaksione || 0),
            amount0: Number(item.Vlera0 || 0),
            amount8: Number(item.Vlera8 || 0),
            amount18: Number(item.Vlera18 || 0),
            totalWithoutVat: Number(item.TotalPaTVSH || 0),
            reassessment: Number(item.Rivleresimi || 0),
            amountWithoutReassessment: Number(item.VleraPaRivleresim || 0),
            totalWithVat: Number(item.VleraMeTVSH || 0),
            rowInfo: item,
        }))
    ), [data, paginationModel.page, paginationModel.pageSize]);

    const summaryFooter = useMemo(() => ({
        labelField: "monthLabel",
        label: "Totali",
        values: {
            transactions: String(totals.totalTransactions || 0),
            amount0: formatCurrency(totals.totalAmount0),
            amount8: formatCurrency(totals.totalAmount8),
            amount18: formatCurrency(totals.totalAmount18),
            totalWithoutVat: formatCurrency(totals.totalWithoutVat),
            reassessment: formatCurrency(totals.totalReassessment),
            amountWithoutReassessment: formatCurrency(totals.totalWithoutReassessment),
            totalWithVat: formatCurrency(totals.totalWithVat),
        },
    }), [totals]);

    return (
        <div className="Crud__Container ARP__page">
            <div className="ARP__content">
                <CrudPageHeader
                    icon={FaShoppingBasket}
                    title="Raporti Mujor i Blerjeve"
                    description="Forma është e përshtatur për kontroll, filtrim dhe analizë profesionale të blerjeve të grumbulluara sipas muajve të vitit fiskal."
                    className="ARP__header"
                />

                <div className="ARP__reportGrid">
                    <ReusableAccountBooksGrid
                        columns={columns}
                        rows={rows}
                        loading={loading}
                        rowCount={rowCount}
                        height={650}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                        sortModel={sortModel}
                        setSortModel={setSortModel}
                        filterModel={filterModel}
                        setFilterModel={setFilterModel}
                        quickFilter={quickFilter}
                        setQuickFilter={setQuickFilter}
                        showViewAction={false}
                        showEditAction={false}
                        showDeleteAction={false}
                        showExportButton={false}
                        summaryFooter={summaryFooter}
                        toolbarActions={(
                            <>
                                <AddActionsButton
                                    text="Kopjo"
                                    onClick={handleCopyAllRows}
                                    icon={<AiOutlineCopy />}
                                    className="RSDG__toolButton ARP__gridActionBtn--copy"
                                />
                                <AddActionsButton
                                    text="Print"
                                    onClick={handlePrintPdf}
                                    icon={<AiOutlinePrinter />}
                                    className="RSDG__toolButton ARP__gridActionBtn--print"
                                />
                                <AddActionsButton
                                    text="PDF"
                                    onClick={exportPdf}
                                    icon={<AiOutlineFilePdf />}
                                    className="RSDG__toolButton ARP__gridActionBtn--pdf"
                                />
                                <AddActionsButton
                                    text="XLSX"
                                    onClick={handleExportXlsx}
                                    icon={<BsFiletypeXls />}
                                    className="RSDG__toolButton ARP__gridActionBtn--xlsx"
                                />
                            </>
                        )}
                    />
                </div>
            </div>
        </div>
    );
}